import React, { useState } from "react";
import "./Header.css";
import { Template } from "./interfaces/template.interface";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmModal from "./ConfirmModal";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store";
import { addTemplate, deleteTemplate, updateTemplate, publishTemplate } from "../store/templatesSlice";
import { ActionType } from "../utils/common.utils";

interface HeaderProps {
  selectedTemplate: Template | undefined;
  onAction: (actionType: ActionType) => void;
}

const Header: React.FC<HeaderProps> = ({ selectedTemplate, onAction }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<{ actionType: ActionType; title: string; body: string }>({
    actionType: "Create",
    title: "Confirm new template",
    body: "Are you sure you want to create new template?",
  });
  const isReadOnly = selectedTemplate?.isPublic;

  const handleClose = () => setShowModal(false);

  const handleAddTemplate = (title: string) => {
    if (!selectedTemplate) {
      toast.error("No template selected!");
      return;
    }

    const newTemplate = {
      title,
      tripType: selectedTemplate.tripType,
      pageFontSize: selectedTemplate?.draft?.pageFontSize,
      pageFontWeight: selectedTemplate?.draft?.pageFontWeight,
      pageFontFamily: selectedTemplate?.draft?.pageFontFamily,
      alternateRow: selectedTemplate?.draft?.alternateRow,
      body: selectedTemplate?.draft?.body,
      columns: selectedTemplate?.draft?.columns,
    };

    dispatch(addTemplate(newTemplate));
    setShowModal(false);
    onAction("Create");
  };

  const handleUpdateTemplate = (title: string) => {
    if (!selectedTemplate?._id) {
      toast.error("No template selected!");
      return;
    }

    const newTemplate = {
      title,
      tripType: selectedTemplate.tripType,
      pageFontSize: selectedTemplate?.draft?.pageFontSize,
      alternateRow: selectedTemplate?.draft?.alternateRow,
      body: selectedTemplate?.draft?.body,
      columns: selectedTemplate?.draft?.columns,
    };

    dispatch(updateTemplate({ id: selectedTemplate._id, updatedTemplate: newTemplate }));
    setShowModal(false);
    onAction("Edit");
  };

  const handlePublishTemplate = () => {
    if (!selectedTemplate?._id) {
      toast.error("No template selected!");
      return;
    }

    dispatch(publishTemplate(selectedTemplate._id));
    setShowModal(false);
    onAction("Publish");
  };

  const handleDeleteTemplate = () => {
    if (!selectedTemplate?._id) {
      toast.error("No template selected!");
      return;
    }

    dispatch(deleteTemplate(selectedTemplate._id));
    setShowModal(false);
    onAction("Delete");
  };

  const openCreateModal = () => {
    setModalConfig({
      actionType: "Create",
      title: "Confirm New Template",
      body: "Are you sure you want to create new template?",
    });
    setShowModal(true);
  };

  const openEditModal = () => {
    setModalConfig({
      actionType: "Edit",
      title: "Confirm changes",
      body: "Are you sure you want to save the selected template changes?",
    });
    setShowModal(true);
  };

  const openPublishModal = () => {
    setModalConfig({
      actionType: "Publish",
      title: "Confirm publish",
      body: "Are you sure you want to publish this template? Devices will receive the published version.",
    });
    setShowModal(true);
  };

  const openDeleteModal = () => {
    setModalConfig({
      actionType: "Delete",
      title: "Confirm Deletion",
      body: "Are you sure you want to delete the selected template?",
    });
    setShowModal(true);
  };

  return (
    <>
      <header className="header">
        <div className="header-actions">
          <button disabled={isReadOnly} className="btn btn-outline-primary" onClick={openEditModal}>
            <i className="fa fa-rocket"></i> Save Edits
          </button>

          <button disabled={isReadOnly} className="btn btn-outline-warning" onClick={openPublishModal}>
            <i className="fa fa-bullhorn"></i> Publish
          </button>

          <button className="btn btn-outline-success" onClick={openCreateModal}>
            <i className="fa fa-rocket"></i> Create New
          </button>

          <button disabled={isReadOnly} className="btn btn-outline-danger" onClick={openDeleteModal}>
            <i className="fa-solid fa-trash"></i> Delete
          </button>
        </div>

        <ConfirmModal
          show={showModal}
          onHide={handleClose}
          templateTitle={selectedTemplate?.title || ""}
          onConfirm={(title: string) => {
            switch (modalConfig.actionType) {
              case "Create":
                handleAddTemplate(title);
                break;
              case "Edit":
                handleUpdateTemplate(title);
                break;
              case "Publish":
                handlePublishTemplate();
                break;
              case "Delete":
                handleDeleteTemplate();
                break;
              default:
                break;
            }
          }}
          title={modalConfig.title}
          body={modalConfig.body}
          actionType={modalConfig.actionType}
        />
      </header>

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default Header;
